// Generated from the validated Extreme scene plan: the camera stage (written by code, not a model).
import React, { Component, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { SCENE_OBJECTS, SHOTS, MOTION, zoneFor, fitDistance } from './LandingShots';
import BruteKeyModel from './BruteKeyModel';

const COMPONENTS = { "brute_key": BruteKeyModel };
const LOOK = {"ambient": 0.85, "key": 1.4, "keyColor": "#ffffff", "rim": 0.8, "rimColor": "#E6197C", "sky": "#ffffff", "ground": "#F4E9DC", "hemi": 0.55, "head": 0.7, "env": 0.6};
const N = SHOTS.length;
const OBJECTS = Object.fromEntries(SCENE_OBJECTS.map((o) => [o.id, o]));
const REST = {
  explode: 0, cutaway: 0, highlight: null, dim: 0, focusPart: null, partExplode: 0, action: 0, scroll: 0, away: 0,
};

const reducedMotion = () => typeof window !== 'undefined' && Boolean(window.matchMedia)
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
// The review capture jumps from shot to shot and wants each one settled, not caught mid-move.
const snapToScroll = () => reducedMotion() || (typeof window !== 'undefined' && window.__xlCapture === true);
// A browser check (window.__xlCheck, set before the page loads) needs React to mount, not pixels: the stage draws no
// frames then, so no shader compiles in software WebGL while the page is checked.
const CHECKED = typeof window !== 'undefined' && window.__xlCheck === true;
const clamp01 = (t) => Math.min(1, Math.max(0, t));

// The page's motion feel (MOTION, from the plan): how the camera travels between shots and when the parts open.
const EASE = {
  linear: (t) => t,
  sine: (t) => 0.5 - 0.5 * Math.cos(Math.PI * t),
  smooth: (t) => t * t * (3 - 2 * t),
  cubic: (t) => (t < 0.5 ? 4 * t * t * t : 1 - ((2 - 2 * t) ** 3) / 2),
  expo: (t) => (t <= 0 ? 0 : t >= 1 ? 1 : t < 0.5 ? 2 ** (20 * t - 10) / 2 : (2 - 2 ** (10 - 20 * t)) / 2),
  out: (t) => 1 - (1 - t) ** 3,
  back: (t) => 1 + 2.4 * (t - 1) ** 3 + 1.4 * (t - 1) ** 2,
  spring: (t) => 1 + 3.2 * (t - 1) ** 3 + 2.2 * (t - 1) ** 2,
};
const HOLD = Math.min(0.45, Math.max(0, Number(MOTION.hold) || 0));
const INERTIA = Math.min(20, Math.max(1.5, Number(MOTION.inertia) || 7));
const MOVE = 1 - 2 * HOLD;
// When the parts open within a move: with the camera, ahead of it, after it settles, springing, or in steps.
const STATE_WINDOW = {
  lead: [HOLD * 0.5, HOLD + MOVE * 0.6, 'out'],
  after: [HOLD + MOVE * 0.45, 1 - HOLD * 0.3, 'sine'],
  spring: [HOLD, 1 - HOLD, 'spring'],
}[MOTION.state] || [HOLD, 1 - HOLD, MOTION.ease === 'back' ? 'smooth' : MOTION.ease];
const STATE_STEPS = MOTION.state === 'steps' ? 4 : 0;

/** Progress through one move: still for the hold at each end, then the feel's ease (optionally in steps). */
function eased(raw, start, end, ease, steps) {
  const t = (EASE[ease] || EASE.smooth)(clamp01((raw - start) / Math.max(0.001, end - start)));
  return steps ? Math.round(t * steps) / steps : t;
}
const vec = (v, fallback) => (Array.isArray(v) && v.length === 3 && v.every(Number.isFinite) ? v : fallback);
const focusOf = (shot) => String((shot && shot.focus) || '').split('.');

/** Whether one object surrounds another (the inner one sits inside its box), so the camera would look through it. */
function contains(outerId, innerId) {
  const outer = OBJECTS[outerId];
  const inner = OBJECTS[innerId];
  if (!outer || !inner || outer === inner || (outer.size || 1) <= (inner.size || 1)) return false;
  const size = outer.size || 1;
  const half = vec(outer.dims, [size, size, size]).map((d) => d / 2);
  const a = vec(outer.position, [0, 0, 0]);
  const b = vec(inner.position, [0, 0, 0]);
  return half.every((h, i) => Math.abs(b[i] - a[i]) <= h);
}
const encloses = (id, shot) => contains(id, focusOf(shot)[0]);
// Inner subsystems stay inside the closed product, out of sight: a battery "inside" a headband floated in the open
// space under its arc (12 Oct 2026). They show when a shot focuses them or opens what holds them.
const INNER = new Set(SCENE_OBJECTS.filter((o) => SCENE_OBJECTS.some((p) => contains(p.id, o.id))).map((o) => o.id));

/** What each object does in a shot: the focused one opens up and performs (the whole product at depth 0), the
 * others recede, and one around it comes off (a watch case faded to a third still hid the escapement it held). */
function stateFor(shot, id) {
  const [focus, part] = focusOf(shot);
  if (!shot || !shot.depth || !focus) {
    return { ...REST, action: (shot && shot.action) || 0, away: INNER.has(id) ? 1 : 0 };
  }
  if (focus !== id) {
    if (encloses(id, shot)) return { ...REST, cutaway: 1, dim: 0.85, away: 1 };
    const opened = (shot.explode || 0) >= 0.3 || (shot.cutaway || 0) >= 0.5;
    if (contains(focus, id)) return { ...REST, away: opened ? 0 : 1 };
    return { ...REST, dim: 0.85, away: INNER.has(id) ? 1 : 0 };
  }
  const inside = shot.depth >= 3;
  return {
    ...REST, explode: shot.explode || 0, cutaway: shot.cutaway || 0, highlight: part || null, dim: 0,
    focusPart: inside ? part || null : null, partExplode: inside ? shot.explode_part || 0 : 0, action: shot.action || 0,
  };
}

/** World position of the piece a shot's copy points at, in that shot's state. */
function anchorOf(shot) {
  const [id, partId, childId] = String(shot.anchor || shot.focus || '').split('.');
  const obj = OBJECTS[id];
  if (!obj) return new THREE.Vector3(...vec(shot.target, [0, 0, 0]));
  const point = new THREE.Vector3(...vec(obj.position, [0, 0, 0]));
  const part = partId ? (obj.parts || []).find((p) => p.id === partId) : null;
  if (part) {
    // One product holds every assembly at its own place (a single-object plan); a part without one sits at the centre.
    point.add(new THREE.Vector3(...vec(part.position, [0, 0, 0])));
    point.addScaledVector(new THREE.Vector3(...vec(part.explode, [0, 0, 0])), shot.explode || 0);
    const child = childId ? (part.children || []).find((c) => c.id === childId) : null;
    if (child) point.addScaledVector(new THREE.Vector3(...vec(child.explode, [0, 0, 0])), shot.explode_part || 0);
  }
  return point;
}

/** Bounding radius of what a shot frames: the whole product, or the focused object as far as it is opened. */
function subjectRadius(shot) {
  const [id] = focusOf(shot);
  const obj = OBJECTS[id];
  const target = vec(shot.target, [0, 0, 0]);
  if (!obj || !shot.depth) {
    // The closed product only: the hidden inner subsystems' bounds made the whole headphone a third smaller.
    return Math.max(0.3, ...SCENE_OBJECTS.filter((o) => !INNER.has(o.id)).map((o) => {
      const p = vec(o.position, [0, 0, 0]);
      return Math.hypot(p[0] - target[0], p[1] - target[1], p[2] - target[2]) + (o.size || 1) / 2;
    }));
  }
  const part = (obj.parts || []).find((p) => p.id === focusOf(shot)[1]);
  const partSize = part && Number.isFinite(part.size) && part.size > 0 ? part.size : 0;
  if (shot.depth >= 3) {
    // Inside one part: the part and its pieces as far as the shot opens them. The camera follows the part out of
    // the object; aimed at the object's centre, an inside shot of a watch's balance wheel showed empty space.
    const pieces = ((part && part.children) || []).map((c) => Math.hypot(...vec(c.explode, [0, 0, 0])));
    return (partSize ? partSize / 2 : (obj.size || 1) * 0.15) + Math.max(0, ...pieces) * (shot.explode_part || 0);
  }
  // One assembly of the product brought forward: framed on its own size, not the whole product's.
  if (shot.depth === 1 && partSize) return partSize / 2;
  const spread = Math.max(0, ...(obj.parts || []).map((p) => Math.hypot(...vec(p.explode, [0, 0, 0]))));
  return (obj.size || 1) / 2 + spread * (shot.explode || 0);
}

const STATES = SHOTS.map((shot) => Object.fromEntries(SCENE_OBJECTS.map((o) => [o.id, stateFor(shot, o.id)])));
const ANCHORS = SHOTS.map(anchorOf);
// Floating labels: the piece each one names, in its shot's state (the labels show only with their shot).
const LABELS = SHOTS.map((shot) => (shot.labels || []).map((label) => anchorOf({ ...shot, anchor: label.anchor })));
const RADII = SHOTS.map(subjectRadius);

/** Each shot's camera for this canvas: the plan's direction, at the distance that fits its zone. */
function framing(width, height) {
  const viewport = typeof window === 'undefined' ? width : window.innerWidth;
  return SHOTS.map((shot, index) => {
    const zone = zoneFor(viewport, shot);
    const planned = new THREE.Vector3(...vec(shot.target, [0, 0, 0]));
    // An inside shot, or one bringing a part forward, looks at the focused part where the shot has moved it, from
    // the planned direction.
    const onPart = shot.depth >= 3 || (shot.depth === 1 && Boolean(focusOf(shot)[1]));
    const target = onPart ? anchorOf({ ...shot, anchor: focusOf(shot).slice(0, 2).join('.') }) : planned;
    const direction = new THREE.Vector3(...vec(shot.camera, [0, 1, 4])).sub(planned);
    direction.normalize();
    const fov = shot.fov || 35;
    const aspect = (width * (zone.zone || 1)) / Math.max(1, height);
    const distance = fitDistance(RADII[index], fov, zone.subjectFill || 0.6, aspect);
    return {
      position: target.clone().addScaledVector(direction, distance), target, fov,
      shift: viewport >= 768 ? (shot.side || 1) * (zone.shift || 0) : 0,
      vshift: viewport >= 768 ? zone.vshift || 0 : 0,
    };
  });
}

/** Fractional shot index for the scroll position, linear between shots (the feel eases it in the rig). */
function shotProgress() {
  const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
  if (N < 2) return 0;
  const anchors = SHOTS.map((shot) => {
    const el = document.querySelector(`[data-shot="${CSS.escape(String(shot.id))}"]`);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return r.top + window.scrollY + r.height / 2 - window.innerHeight / 2;
  });
  if (anchors.some((a) => a === null)) return (Math.min(1, window.scrollY / max)) * (N - 1);
  const y = window.scrollY;
  if (y <= anchors[0]) return 0;
  for (let i = 0; i < N - 1; i += 1) {
    if (y < anchors[i + 1]) return i + (y - anchors[i]) / Math.max(1, anchors[i + 1] - anchors[i]);
  }
  return N - 1;
}

function publish(cache, name, value) {
  if (!Number.isFinite(value) || Math.abs((cache[name] ?? -1e9) - value) < 0.5) return;
  cache[name] = value;
  document.documentElement.style.setProperty(name, `${value.toFixed(1)}px`);
}

// Exploded and inside views are framed on what the models really draw, not only on the plan's estimate: a driver's
// diaphragm, planned as a small part, filled the frame and its magnet ran off the screen (12 Oct 2026, 307f6ebc).
const FIT_PULL_MAX = 3;
const FIT_MARGIN = 0.04;
const CORNERS = [0, 1, 2, 3, 4, 5, 6, 7].map((k) => [k % 2, Math.floor(k / 2) % 2, Math.floor(k / 4)]);

/** Screen box (canvas px) of everything an object draws now, from each mesh's own bounds; null when it draws none. */
function drawnBox(root, camera, size, point) {
  let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity; let behind = false;
  if (root) {
    root.traverseVisible((node) => {
      if (!node.isMesh || !node.geometry) return;
      if (!node.geometry.boundingBox) node.geometry.computeBoundingBox();
      const { min, max } = node.geometry.boundingBox;
      CORNERS.forEach(([cx, cy, cz]) => {
        point.set(cx ? max.x : min.x, cy ? max.y : min.y, cz ? max.z : min.z).applyMatrix4(node.matrixWorld)
          .project(camera);
        behind = behind || point.z > 1;
        const px = ((point.x + 1) / 2) * size.width;
        const py = ((1 - point.y) / 2) * size.height;
        x0 = Math.min(x0, px); x1 = Math.max(x1, px); y0 = Math.min(y0, py); y1 = Math.max(y1, py);
      });
    });
  }
  return Number.isFinite(x0) ? { x0, y0, x1, y1, behind } : null;
}

/** Where a fixed site header ends: a subject under it is hidden. */
function headerBottom() {
  const header = document.querySelector('header');
  if (!header) return 0;
  const { position } = getComputedStyle(header);
  const r = header.getBoundingClientRect();
  return (position === 'fixed' || position === 'sticky') && r.top <= 0 ? Math.max(0, r.bottom) : 0;
}

function Rig({ states }) {
  const { camera, size, invalidate, scene } = useThree();
  const frames = useMemo(() => framing(size.width, size.height), [size.width, size.height]);
  const curves = useMemo(() => (N > 1 ? {
    position: new THREE.CatmullRomCurve3(frames.map((f) => f.position), false, 'centripetal'),
    target: new THREE.CatmullRomCurve3(frames.map((f) => f.target), false, 'centripetal'),
  } : null), [frames]);
  const motion = useRef({ goal: 0, now: 0, settleUntil: 0, shot: -1, vars: {}, pull: SHOTS.map(() => 1) });
  const tmp = useMemo(() => ({
    pos: new THREE.Vector3(), look: new THREE.Vector3(), anchor: new THREE.Vector3(),
    right: new THREE.Vector3(), up: new THREE.Vector3(), corner: new THREE.Vector3(), label: new THREE.Vector3(),
    a: new THREE.Vector3(), b: new THREE.Vector3(),
    sa: new THREE.Spherical(), sb: new THREE.Spherical(), sc: new THREE.Spherical(),
  }), []);

  useEffect(() => {
    const onScroll = () => {
      motion.current.goal = shotProgress();
      motion.current.settleUntil = performance.now() + 1200;
      invalidate();
    };
    onScroll();
    motion.current.now = motion.current.goal;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [invalidate]);

  useFrame((_, delta) => {
    if (!N) return;
    const m = motion.current;
    m.now = snapToScroll() ? m.goal : m.now + (m.goal - m.now) * (1 - Math.exp(-INERTIA * Math.min(delta, 0.1)));
    if (Math.abs(m.goal - m.now) < 0.0005) m.now = m.goal;
    const r = Math.min(N - 1, Math.max(0, m.now));
    const i = Math.floor(r);
    const j = Math.min(N - 1, i + 1);
    const cu = eased(r - i, HOLD, 1 - HOLD, MOTION.ease, MOTION.steps);
    const su = eased(r - i, STATE_WINDOW[0], STATE_WINDOW[1], STATE_WINDOW[2], STATE_STEPS);
    const u = clamp01(cu);
    const arc = Math.sin(Math.PI * u);
    const sway = i % 2 ? -1 : 1;

    if (curves && (MOTION.path === 'spline' || MOTION.path === 'crane')) {
      const t = clamp01((i + cu) / (N - 1));
      curves.position.getPoint(t, tmp.pos);
      curves.target.getPoint(t, tmp.look);
    } else if (MOTION.path === 'orbit') {
      // Round the subject: radius and height blend, the angle takes the short way round and swings wider mid-move.
      tmp.look.lerpVectors(frames[i].target, frames[j].target, cu);
      tmp.sa.setFromVector3(tmp.a.subVectors(frames[i].position, frames[i].target));
      tmp.sb.setFromVector3(tmp.b.subVectors(frames[j].position, frames[j].target));
      const turn = ((tmp.sb.theta - tmp.sa.theta + 3 * Math.PI) % (2 * Math.PI)) - Math.PI;
      tmp.sc.set(tmp.sa.radius + (tmp.sb.radius - tmp.sa.radius) * cu, tmp.sa.phi + (tmp.sb.phi - tmp.sa.phi) * cu,
        tmp.sa.theta + turn * cu + sway * (Number(MOTION.swing) || 0) * arc);
      tmp.pos.setFromSpherical(tmp.sc).add(tmp.look);
    } else {
      tmp.pos.lerpVectors(frames[i].position, frames[j].position, cu);
      tmp.look.lerpVectors(frames[i].target, frames[j].target, cu);
    }
    if (MOTION.lift) tmp.pos.y += Number(MOTION.lift) * (RADII[i] + RADII[j]) * arc;
    // Pull back from the subject as far as this shot needs to keep its card clear (see the overlap guard below).
    const pull = m.pull[i] + (m.pull[j] - m.pull[i]) * u;
    tmp.pos.sub(tmp.look).multiplyScalar(pull).add(tmp.look);
    camera.position.copy(tmp.pos);
    camera.lookAt(tmp.look);
    // The feel's bank and zoom breathe only mid-move: every shot is framed level and at its own lens.
    if (MOTION.roll) camera.rotateZ(sway * Number(MOTION.roll) * (Math.PI / 180) * arc);
    camera.fov = frames[i].fov + (frames[j].fov - frames[i].fov) * u + (Number(MOTION.fov) || 0) * arc;
    const shift = frames[i].shift + (frames[j].shift - frames[i].shift) * u;
    const vshift = frames[i].vshift + (frames[j].vshift - frames[i].vshift) * u;
    if (shift || vshift) {
      camera.setViewOffset(size.width, size.height, -shift * size.width, -vshift * size.height, size.width, size.height);
    } else {
      camera.clearViewOffset();
    }
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld();

    SCENE_OBJECTS.forEach((o) => {
      const s = states[o.id].current;
      const a = STATES[i][o.id];
      const b = STATES[j][o.id];
      // A spring may carry the parts past their mark and back; they never pass through each other below zero.
      s.explode = Math.max(0, a.explode + (b.explode - a.explode) * su);
      s.partExplode = Math.max(0, a.partExplode + (b.partExplode - a.partExplode) * su);
      s.cutaway = clamp01(a.cutaway + (b.cutaway - a.cutaway) * su);
      s.away = clamp01(a.away + (b.away - a.away) * su);
      s.dim = clamp01(a.dim + (b.dim - a.dim) * u);
      // The product's own behaviour (a pump pulsing, a portafilter locking) and what turns while the page scrolls.
      s.action = clamp01(a.action + (b.action - a.action) * su);
      s.scroll = r;
      const near = su < 0.5 ? a : b;
      s.highlight = near.highlight;
      s.focusPart = near.focusPart;
    });

    tmp.anchor.copy(ANCHORS[i]).lerp(ANCHORS[j], clamp01(su)).project(camera);
    publish(m.vars, '--xl-ax', ((tmp.anchor.x + 1) / 2) * size.width);
    publish(m.vars, '--xl-ay', ((1 - tmp.anchor.y) / 2) * size.height);
    const radius = RADII[i] + (RADII[j] - RADII[i]) * clamp01(su);
    tmp.right.setFromMatrixColumn(camera.matrixWorld, 0);
    tmp.up.setFromMatrixColumn(camera.matrixWorld, 1);
    let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
    [[1, 1], [1, -1], [-1, 1], [-1, -1]].forEach(([sx, sy]) => {
      tmp.corner.copy(tmp.look).addScaledVector(tmp.right, sx * radius).addScaledVector(tmp.up, sy * radius).project(camera);
      const px = ((tmp.corner.x + 1) / 2) * size.width;
      const py = ((1 - tmp.corner.y) / 2) * size.height;
      x0 = Math.min(x0, px); x1 = Math.max(x1, px); y0 = Math.min(y0, py); y1 = Math.max(y1, py);
    });
    publish(m.vars, '--xl-bx0', x0); publish(m.vars, '--xl-by0', y0);
    publish(m.vars, '--xl-bx1', x1); publish(m.vars, '--xl-by1', y1);
    LABELS[Math.round(r)].forEach((point, k) => {
      tmp.label.copy(point).project(camera);
      publish(m.vars, `--xl-l${k}x`, ((tmp.label.x + 1) / 2) * size.width);
      publish(m.vars, `--xl-l${k}y`, ((1 - tmp.label.y) / 2) * size.height);
    });

    // Overlap guard: the visible card must never cover the subject. While they intersect, this shot's camera eases
    // back a little each frame (up to 1.8x), so no screen size can end with copy over the product.
    const near = Math.round(r);
    const card = document.querySelector(`[data-shot="${CSS.escape(String(SHOTS[near].id))}"] > .xl-card`);
    if (card && Math.abs(i + cu - near) < 0.02) {
      const r = card.getBoundingClientRect();
      const ox = Math.min(r.right, x1) - Math.max(r.left, x0);
      const oy = Math.min(r.bottom, y1) - Math.max(r.top, y0);
      if (ox > 4 && oy > 4 && m.pull[near] < 1.8) {
        m.pull[near] = Math.min(1.8, m.pull[near] * 1.03);
        m.settleUntil = performance.now() + 400;
      }
    }

    // What the shot really draws: while any piece of an exploded or inside view leaves the canvas or slides under
    // the header, this shot's camera eases back (up to 3x), so no screen size ends with the subject cut off.
    const settled = Math.abs(i + cu - near) < 0.02;
    if (settled && (SHOTS[near].depth || 0) >= 2 && m.pull[near] < FIT_PULL_MAX) {
      const drawn = drawnBox(scene.getObjectByName(`xl-${focusOf(SHOTS[near])[0]}`), camera, size, tmp.corner);
      const pad = FIT_MARGIN * Math.min(size.width, size.height);
      if (drawn && (drawn.behind || drawn.x0 < pad || drawn.x1 > size.width - pad
        || drawn.y0 < headerBottom() + pad || drawn.y1 > size.height - pad)) {
        m.pull[near] = Math.min(FIT_PULL_MAX, m.pull[near] * 1.04);
        m.settleUntil = performance.now() + 400;
      }
    }

    const index = Math.round(r);
    if (index !== m.shot) {
      m.shot = index;
      const shot = SHOTS[index];
      window.dispatchEvent(new CustomEvent('xl-shot', { detail: { id: shot.id, index, depth: shot.depth } }));
    }
    if (m.goal !== m.now || performance.now() < m.settleUntil) invalidate();
  });
  return null;
}

/** Each object at its planned size, centred where it belongs, however its builder scaled or placed it: measured
 * once, assembled, on its first frame (a movement modelled 68x too large filled the whole scene, 12 Oct 2026). */
function Fit({ size, children }) {
  const ref = useRef(null);
  const fitted = useRef(false);
  useFrame(() => {
    const group = ref.current;
    if (fitted.current || !group || !group.parent) return;
    const box = new THREE.Box3().setFromObject(group);
    if (box.isEmpty()) return;
    fitted.current = true;
    const extent = Math.max(...box.getSize(new THREE.Vector3()).toArray());
    const scale = extent > 0 ? Math.min(40, Math.max(0.025, size / extent)) : 1;
    if (Math.abs(scale - 1) < 0.15) return;
    const center = group.parent.worldToLocal(box.getCenter(new THREE.Vector3()));
    group.scale.setScalar(scale);
    group.position.copy(center).multiplyScalar(-scale);
  });
  return <group ref={ref}>{children}</group>;
}

/** One broken object must not blank the whole page: it is dropped and reported. */
class PieceBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error(`[extreme-stage] 3D object ${this.props.id} failed: ${error && error.message}`);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/** A light at the camera, after the rig has moved it: whatever a shot looks at is lit, an underside included. */
function Headlight({ intensity }) {
  const light = useRef();
  const { camera, scene } = useThree();
  const ahead = useMemo(() => new THREE.Vector3(), []);
  useEffect(() => {
    const target = light.current && light.current.target;
    if (!target) return undefined;
    scene.add(target);
    return () => scene.remove(target);
  }, [scene]);
  useFrame(() => {
    if (!light.current) return;
    light.current.position.copy(camera.position);
    light.current.target.position.copy(camera.position).add(camera.getWorldDirection(ahead));
    light.current.target.updateMatrixWorld();
  });
  return <directionalLight ref={light} intensity={intensity} />;
}

/** Studio reflections for every material: three's procedural room, prefiltered once. */
function StudioEnvironment({ intensity }) {
  const { gl, scene, invalidate } = useThree();
  useEffect(() => {
    if (!intensity) return undefined;
    const pmrem = new THREE.PMREMGenerator(gl);
    const room = new RoomEnvironment();
    const map = pmrem.fromScene(room, 0.04).texture;
    room.dispose();
    pmrem.dispose();
    scene.environment = map;
    scene.environmentIntensity = intensity;
    invalidate();
    return () => {
      if (scene.environment === map) scene.environment = null;
      map.dispose();
    };
  }, [gl, scene, invalidate, intensity]);
  return null;
}

/** An object in its place; taken off while it encloses the shot's focus (it opens first if it can cut away). */
function Placed({ object, stateRef, children }) {
  const group = useRef();
  useFrame(() => {
    if (group.current) group.current.visible = !(stateRef.current.away > 0.5);
  });
  return <group ref={group} name={`xl-${object.id}`} position={vec(object.position, [0, 0, 0])}>{children}</group>;
}

export default function LandingStage() {
  const states = useMemo(() => Object.fromEntries(SCENE_OBJECTS.map((o) => [o.id, { current: { ...REST } }])), []);
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const band = width < 768 ? zoneFor(width, SHOTS[0]).band : '100vh';
  return (
    <div className="extreme-stage" aria-hidden="true" style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: band, zIndex: 0, pointerEvents: 'none',
    }}>
      <Canvas frameloop={CHECKED ? 'never' : 'demand'} dpr={[1, 1.5]} gl={{ antialias: true, alpha: true }}
        camera={{ fov: (SHOTS[0] && SHOTS[0].fov) || 35, near: 0.01, far: 200, position: [0, 1, 4] }}>
        <ambientLight intensity={LOOK.ambient} />
        <hemisphereLight args={[LOOK.sky, LOOK.ground, LOOK.hemi]} />
        <directionalLight position={[3, 5, 4]} intensity={LOOK.key} color={LOOK.keyColor} />
        <directionalLight position={[-4, 2, -3]} intensity={LOOK.rim} color={LOOK.rimColor} />
        <StudioEnvironment intensity={LOOK.env || 0} />
        {SCENE_OBJECTS.map((o) => {
          const Piece = COMPONENTS[o.id];
          return Piece ? (
            <PieceBoundary key={o.id} id={o.id}>
              <Placed object={o} stateRef={states[o.id]}>
                <Fit size={o.size || 1}><Piece stateRef={states[o.id]} /></Fit>
              </Placed>
            </PieceBoundary>
          ) : null;
        })}
        <Rig states={states} />
        <Headlight intensity={LOOK.head || 0} />
      </Canvas>
    </div>
  );
}
